// ── Due Diligence + POA content of the tenant, browser side (multi-tenant, package ddpoa) ──────────
// The zoning map + essay and service links a DD report prints, the site-inspector names, and what a new
// POA is pre-filled with are workspace settings (server: services/ddPoaContent.ts) — the client spells
// none of them. Each is fetched once per page load and shared; empty until it has answered (and when the
// tenant has set none: the report then prints no essay / no service list, a new POA starts blank).
import { useEffect, useState } from 'react';
import { ddPoaApi, DdEditorSettings, DdContent, PoaDefaults } from './api/ddpoa';

export type { DdEditorSettings, DdContent, PoaDefaults };
export const EMPTY_DD_CONTENT: DdEditorSettings = { zoning: { mapImage: '', paragraphs: [] }, services: [], inspectors: [] };

const tidy = (c: any): DdEditorSettings => ({
  zoning: {
    mapImage: String(c?.zoning?.mapImage || ''),
    paragraphs: Array.isArray(c?.zoning?.paragraphs) ? c.zoning.paragraphs.filter((p: any) => p && typeof p.t === 'string') : [],
  },
  services: Array.isArray(c?.services) ? c.services.filter((s: any) => s && s.name && s.url) : [],
  inspectors: Array.isArray(c?.inspectors) ? c.inspectors.map((x: any) => String(x)).filter(Boolean) : [],
});

// ── DD ────────────────────────────────────────────────────────────────────────────────────────────
let ddCache: DdEditorSettings | null = null;
let ddPending: Promise<DdEditorSettings> | null = null;
// The public client page (no login) hands in the content its own payload carried; while it is set the
// hook returns it and never calls the authed endpoint.
let ddOverride: DdEditorSettings | null = null;
const ddListeners = new Set<(c: DdEditorSettings) => void>();
const ddNotify = () => { const c = ddOverride || ddCache || EMPTY_DD_CONTENT; ddListeners.forEach(f => f(c)); };

export function setDdContentOverride(c: Partial<DdContent> | null | undefined): void {
  ddOverride = c ? tidy({ ...c, inspectors: [] }) : null;
  ddNotify();
}
export function loadDdContent(): Promise<DdEditorSettings> {
  if (ddOverride) return Promise.resolve(ddOverride);
  if (ddCache) return Promise.resolve(ddCache);
  if (!ddPending) {
    ddPending = ddPoaApi.ddSettings()
      .then(c => { ddCache = tidy(c); ddNotify(); return ddCache; })
      .catch(() => { ddPending = null; return EMPTY_DD_CONTENT; });   // retried by the next caller
  }
  return ddPending;
}
/** The DD content now (the override, else the loaded settings, else empty) — outside a component. */
export const ddContentNow = (): DdEditorSettings => ddOverride || ddCache || EMPTY_DD_CONTENT;
/** The DD content, re-rendering the caller once it has loaded. */
export function useDdContent(): DdEditorSettings {
  const [c, setC] = useState<DdEditorSettings>(ddContentNow());
  useEffect(() => {
    let live = true;
    const f = (x: DdEditorSettings) => { if (live) setC(x); };
    ddListeners.add(f);
    setC(ddContentNow());
    if (!ddOverride && !ddCache) void loadDdContent().then(f);
    return () => { live = false; ddListeners.delete(f); };
  }, []);
  return c;
}

// ── POA ───────────────────────────────────────────────────────────────────────────────────────────
const bi = (v: any) => ({ en: String(v?.en || ''), th: String(v?.th || '') });
// The stored setting's `review` part is deliberately dropped here: the three review lines are no
// longer pre-filled boxes but a recorded sign-off (the 'poa_signer' role), so a new POA carries no
// review names at all.
const tidyPoa = (d: any): PoaDefaults => ({
  madeAt: bi(d?.madeAt),
  donee: { name: bi(d?.donee?.name), age: String(d?.donee?.age || ''), idNo: String(d?.donee?.idNo || ''), addr: bi(d?.donee?.addr) },
  witnesses: (Array.isArray(d?.witnesses) ? d.witnesses : []).map((w: any) => ({ name: bi(w?.name) })),
});
export const EMPTY_POA_DEFAULTS: PoaDefaults = tidyPoa(null);
let poaCache: PoaDefaults | null = null;
let poaPending: Promise<PoaDefaults> | null = null;
/** What a new POA is pre-filled with. Never rejects: a failed load answers with the empty defaults. */
export function loadPoaDefaults(): Promise<PoaDefaults> {
  if (poaCache) return Promise.resolve(poaCache);
  if (!poaPending) {
    poaPending = ddPoaApi.poaDefaults()
      .then(d => { poaCache = tidyPoa(d); return poaCache; })
      .catch(() => { poaPending = null; return EMPTY_POA_DEFAULTS; });   // a new POA then starts blank; retried next time
  }
  return poaPending;
}
